// Screen 10 — Meal-plan generation.
//
// Calls the trusted Supabase RPC public.generate_meal_plans as the
// authenticated customer.
//
// Freshness protocol (Phase B4 — stale-generation fix):
//   1. Await flushDraftNow() so the customer's LATEST planner inputs
//      are persisted server-side BEFORE generation. If the flush fails
//      with a stale_revision (or any non-recoverable draft state) we
//      show the retry state instead of generating from stale data.
//   2. Compute a deterministic generation-input fingerprint from ONLY
//      generation-relevant planner fields. If the stored fingerprint
//      matches, we reuse the same generation_request_id (idempotent
//      replay). Otherwise a fresh UUID is minted and any cached
//      generation result is cleared before the RPC begins.
//   3. Every in-flight run pins the "active" request_id in a ref. A
//      late-completing older response cannot overwrite a newer active
//      generation.
//
// The browser never performs allergy/restriction filtering, planner
// eligibility inference, or recommendation scoring. All of that lives
// inside the trusted server-side generator.

import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { WizardScreen } from '../../components/planner/WizardScreen';
import { BrandButton } from '../../components/BrandButton';
import { useSession } from '../../lib/useSession';
import { useDraft } from '../../lib/plannerDraft';
import { flushDraftNow } from '../../lib/plannerDraftSync';
import {
  getOrCreateGenerationRequestIdForFingerprint,
  startNewGenerationAttempt,
  invokeGenerateMealPlans,
  writeCachedGenerationResult,
  clearCachedGenerationResult,
  computeGenerationFingerprint,
} from '../../lib/mealGeneration';
import { readOutcome, readPlans } from '../../lib/mealPlanReader';

const STATUS_LINES = [
  'Saving your latest choices',
  'Checking meals against your allergies',
  'Balancing your macros',
  'Sorting by your preferences',
  'Composing three plan options',
];

// Lightweight, PII-free diagnostic trace for the flush -> generate
// continuation path. No draft payloads, tokens, or secrets are logged
// -- only outcome/error codes and booleans.
function traceLog(label, payload) {
  try {
    console.info('[etff:generate-trace]', label, payload || {});
  } catch {
    /* ignore */
  }
}

function StateCard({ eyebrow, title, color, children, testid }) {
  return (
    <div
      className="p-4 mt-2"
      style={{
        border: `1px solid ${color}`,
        borderRadius: 'var(--radius)',
        background: 'var(--paper)',
      }}
      data-testid={testid}
    >
      <div className="eyebrow" style={{ color }}>{eyebrow}</div>
      <div className="font-display text-2xl mt-1" style={{ color: 'var(--ink)' }}>
        {title}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function Step10Generating() {
  const { session, loading: sessionLoading, configured } = useSession();
  const { draft } = useDraft();
  const nav = useNavigate();

  const [state, setState] = useState('loading'); // loading | error | no_meals
  const [statusIdx, setStatusIdx] = useState(0);
  const [showCancel, setShowCancel] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const rotateTimer = useRef(null);
  const cancelTimer = useRef(null);
  const runningRef = useRef(false);
  const startedRef = useRef(false);
  // The generation_request_id currently authoritative. Any RPC
  // response that echoes a different id will be discarded (guards
  // against a late-completing older request stomping newer results).
  const activeReqIdRef = useRef(null);

  function stopTimers() {
    if (rotateTimer.current) { clearInterval(rotateTimer.current); rotateTimer.current = null; }
    if (cancelTimer.current) { clearTimeout(cancelTimer.current); cancelTimer.current = null; }
  }

  // Force flush, compute fingerprint, resolve request id, then run.
  // If `force === true`, ALWAYS rotate to a new UUID (used by the
  // "Start a fresh attempt" button).
  async function beginRun({ force = false } = {}) {
    if (runningRef.current) return;
    runningRef.current = true;
    setState('loading');
    setErrorMsg('');
    setStatusIdx(0);
    setShowCancel(false);
    stopTimers();
    rotateTimer.current = setInterval(
      () => setStatusIdx((i) => (i + 1) % STATUS_LINES.length),
      1200
    );
    cancelTimer.current = setTimeout(() => setShowCancel(true), 12000);

    // ---- Step 1: Persist the latest draft BEFORE generating. -----
    traceLog('flush:start');
    const flushResult = await flushDraftNow();
    traceLog('flush:result', { ok: flushResult.ok, outcome: flushResult.outcome || null });
    if (!flushResult.ok) {
      stopTimers();
      runningRef.current = false;
      // Customer-safe messages per outcome.
      let msg = 'We could not save your latest changes. Please try again.';
      if (flushResult.outcome === 'stale_revision' || flushResult.outcome === 'existing_auth_draft') {
        msg = 'Your plan was updated in another tab. Please refresh and try again.';
      } else if (flushResult.outcome === 'onboarding_pending') {
        msg = 'Finishing your account setup. Please try again in a moment.';
      }
      traceLog('abort:flush_not_ok', { outcome: flushResult.outcome || null });
      setErrorMsg(msg);
      setState('error');
      return;
    }

    // ---- Step 2: Fingerprint the current draft & resolve the UUID.
    const fingerprint = computeGenerationFingerprint(draft);
    let reqId;
    let rotated = false;
    if (force) {
      reqId = startNewGenerationAttempt(fingerprint);
      rotated = true;
    } else {
      const resolved = getOrCreateGenerationRequestIdForFingerprint(fingerprint);
      reqId = resolved.id;
      rotated = !!resolved.rotated;
      // If rotated, the cached result was already cleared inside
      // mealGeneration so /plan/results cannot render an old plan.
    }
    activeReqIdRef.current = reqId;
    traceLog('request_id:resolved', { rotated, forced: force });

    // ---- Step 3: Invoke the RPC. -----------------------------------
    traceLog('generate_meal_plans:calling');
    const result = await invokeGenerateMealPlans(reqId);
    traceLog('generate_meal_plans:returned', { ok: result.ok, error: result.ok ? null : result.error });

    // Guard: if a newer generation has started since we launched, drop
    // this response. This can happen if the customer clicked
    // "Start a fresh attempt" while the previous RPC was still running.
    if (result.requestId !== activeReqIdRef.current) {
      traceLog('abort:stale_active_request_id');
      runningRef.current = false;
      return;
    }

    stopTimers();
    runningRef.current = false;

    if (!result.ok) {
      traceLog('abort:rpc_not_ok', { error: result.error });
      setErrorMsg(result.error);
      setState('error');
      return;
    }
    const payload = result.data;
    const outcome = readOutcome(payload);
    const plans = readPlans(payload);
    const isSuccess = outcome === 'generated' || outcome === 'replayed';
    traceLog('generate_meal_plans:outcome', { outcome: outcome || null, plan_count: plans.length });
    if (outcome === 'no_eligible_meals' || (isSuccess && plans.length === 0)) {
      writeCachedGenerationResult(payload);
      setState('no_meals');
      return;
    }
    if (!isSuccess || plans.length === 0) {
      traceLog('abort:not_success_or_empty', { outcome: outcome || null, plan_count: plans.length });
      setErrorMsg('We could not build your plans. Please try again in a moment.');
      setState('error');
      return;
    }
    traceLog('success:navigating_to_results');
    writeCachedGenerationResult(payload);
    nav('/plan/results', { replace: true });
  }

  useEffect(() => {
    if (sessionLoading) return;
    if (!session) return;               // <Navigate/> below handles this
    if (startedRef.current) return;
    startedRef.current = true;
    // NOTE: do NOT clear the cached generation result here. If the
    // draft flush fails, the customer stays on this screen with a
    // retry state and the previous cached result must remain
    // available for /plan/results (it still belongs to the customer's
    // latest fully-generated attempt). The cache is only cleared
    // AFTER a genuine new attempt is committed, inside
    // getOrCreateGenerationRequestIdForFingerprint / startNewGenerationAttempt.
    beginRun();
    return () => {
      stopTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionLoading]);

  function retrySameAttempt() {
    startedRef.current = true;
    beginRun();
  }

  function retryFreshAttempt() {
    startedRef.current = true;
    beginRun({ force: true });
  }

  if (!configured) {
    return (
      <WizardScreen step={9} eyebrow="Building your plans" title="Not available">
        <p style={{ color: 'var(--text-dim)' }}>
          The planner is temporarily unavailable. Please try again later.
        </p>
      </WizardScreen>
    );
  }
  if (sessionLoading) return null;
  if (!session) return <Navigate to="/sign-in" replace state={{ returnTo: '/plan/generate' }} />;

  return (
    <WizardScreen
      step={9}
      eyebrow="Building your plans"
      title={state === 'loading' ? 'One moment…' : 'Plan generation'}
      canContinue={false}
      hideBack={state === 'loading'}
      submitLabel=""
    >
      {state === 'loading' && (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col items-center text-center py-10"
          data-testid="generating-loading"
        >
          <div
            className="mb-6"
            style={{
              width: 64, height: 64,
              border: '4px solid var(--cream-2)',
              borderTopColor: 'var(--orange)',
              borderRadius: '50%',
              animation: 'spin 0.9s linear infinite',
            }}
          />
          <div className="eyebrow">Working</div>
          <div
            className="font-display uppercase mt-3"
            style={{ fontSize: '22px', letterSpacing: '0.06em' }}
            data-testid="generating-status-line"
          >
            {STATUS_LINES[statusIdx]}
          </div>
          {showCancel && (
            <div className="mt-6">
              <BrandButton
                variant="outline"
                size="sm"
                onClick={() => nav('/plan/preferences')}
                data-testid="generating-cancel-btn"
              >
                Cancel
              </BrandButton>
            </div>
          )}
        </div>
      )}

      {state === 'no_meals' && (
        <StateCard
          color="var(--warning, #b26a00)"
          eyebrow="No match"
          title="We couldn't build a plan"
          testid="generating-no-meals"
        >
          <p style={{ color: 'var(--text-dim)' }}>
            Your allergies and restrictions filtered out too many meals. Try
            adjusting them and we&rsquo;ll rebuild your plans.
          </p>
          <div className="mt-4 flex gap-3 flex-wrap">
            <BrandButton
              variant="primary"
              size="sm"
              onClick={() => nav('/plan/allergies')}
              data-testid="generating-adjust-allergies-btn"
            >
              Adjust allergies
            </BrandButton>
            <BrandButton
              variant="outline"
              size="sm"
              onClick={() => nav('/plan/restrictions')}
              data-testid="generating-adjust-restrictions-btn"
            >
              Adjust restrictions
            </BrandButton>
          </div>
        </StateCard>
      )}

      {state === 'error' && (
        <StateCard
          color="var(--danger, #b03a2e)"
          eyebrow="Something went wrong"
          title="We couldn't build your plans"
          testid="generating-error"
        >
          <p style={{ color: 'var(--text-dim)' }} data-testid="generating-error-msg">
            {errorMsg}
          </p>
          <div className="mt-4 flex gap-3 flex-wrap">
            <BrandButton
              variant="primary"
              size="sm"
              onClick={retrySameAttempt}
              data-testid="generating-retry-btn"
            >
              Try again
            </BrandButton>
            <BrandButton
              variant="outline"
              size="sm"
              onClick={retryFreshAttempt}
              data-testid="generating-fresh-attempt-btn"
            >
              Start a fresh attempt
            </BrandButton>
          </div>
        </StateCard>
      )}
    </WizardScreen>
  );
}
